import type { ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, CircleDashed, XCircle } from 'lucide-react'

export function StatusBadge({ status, label }: { status: string; label?: string }) {
  const content = label ?? status.replaceAll('_', ' ')
  return <span className={`status-badge status-${status}`}>{content}</span>
}

export function HealthIcon({ status }: { status: string }) {
  if (status === 'ok') return <CheckCircle2 className="health-icon health-ok" aria-label="Healthy" />
  if (status === 'failed') return <XCircle className="health-icon health-failed" aria-label="Failed" />
  if (status === 'warning') return <AlertTriangle className="health-icon health-warning" aria-label="Needs attention" />
  return <CircleDashed className="health-icon health-unknown" aria-label="Not connected" />
}

export function ScoreBar({ value, tone = 'copper' }: { value: number; tone?: 'copper' | 'blue' | 'green' }) {
  return (
    <div className="score-wrap" aria-label={`${Math.round(value)} out of 100`}>
      <div className="score-track"><div className={`score-fill score-${tone}`} style={{ width: `${Math.max(0, Math.min(value, 100))}%` }} /></div>
      <span>{Math.round(value)}</span>
    </div>
  )
}

export function Sparkline({ values }: { values: number[] }) {
  const max = Math.max(...values, 1)
  const points = values.map((value, index) => `${(index / Math.max(values.length - 1, 1)) * 100},${34 - (value / max) * 30}`).join(' ')
  return (
    <svg className="sparkline" viewBox="0 0 100 36" preserveAspectRatio="none" aria-label="Recent growth">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

export function Card({ children, className = '', title, action }: { children: ReactNode; className?: string; title?: string; action?: ReactNode }) {
  return (
    <section className={`card ${className}`}>
      {(title || action) && <div className="card-heading"><h2>{title}</h2>{action}</div>}
      {children}
    </section>
  )
}

export function EmptyState({ title, body }: { title: string; body: string }) {
  return <div className="empty-state"><CircleDashed /><h3>{title}</h3><p>{body}</p></div>
}

export function Toggle({ checked, onChange, label, help }: { checked: boolean; onChange: (checked: boolean) => void; label: string; help?: string }) {
  return (
    <label className="toggle-row">
      <span><strong>{label}</strong>{help && <small>{help}</small>}</span>
      <button type="button" className={`toggle ${checked ? 'is-on' : ''}`} onClick={() => onChange(!checked)} aria-pressed={checked}><i /></button>
    </label>
  )
}

export function Field({ label, help, children }: { label: string; help?: string; children: ReactNode }) {
  return <label className="field"><span>{label}</span>{children}{help && <small>{help}</small>}</label>
}

